import { createAction } from '@reduxjs/toolkit';
import {
  SystemProcessExecution,
  SystemProcessExecutionSearchCriteria,
} from '@ots-frontend/ots-data';
import { OtsDataTableSortDirection } from '../components/OtsDataTable';
import { sortOtsDataTableRows } from '../components/OtsDataTableCells';

export type SystemProcessExecutionSortColumn =
  | 'process'
  | 'status'
  | 'type'
  | 'started'
  | 'outcomes';
export type SystemProcessExecutionOutcomeFilter =
  | ''
  | 'failed'
  | 'warning'
  | 'successful';

export type SystemProcessExecutionColumnFilters = {
  process: string;
  status: string;
  type: string;
  outcomes: SystemProcessExecutionOutcomeFilter;
};

export type SystemProcessExecutionTableState = {
  sortColumn: SystemProcessExecutionSortColumn;
  sortDirection: OtsDataTableSortDirection;
  filters: SystemProcessExecutionColumnFilters;
};

export type SystemProcessExecutionUIStoreState = {
  searchFilter?: SystemProcessExecutionSearchCriteria;
  tableState?: SystemProcessExecutionTableState;
};

export const defaultSystemProcessExecutionTableState: SystemProcessExecutionTableState =
  {
    sortColumn: 'started',
    sortDirection: 'desc',
    filters: { process: '', status: '', type: '', outcomes: '' },
  };

// These action names intentionally extend the existing shared ots-ui slice so all host apps retain
// the view while navigating and clear it through their normal logout reset.
export const setSystemProcessExecutionSearchFilter =
  createAction<SystemProcessExecutionSearchCriteria>(
    'ots-ui/system-process-execution/setSearchFilter'
  );
export const setSystemProcessExecutionTableState =
  createAction<SystemProcessExecutionTableState>(
    'ots-ui/system-process-execution/setTableState'
  );

const outcomeTotal = (execution: SystemProcessExecution) =>
  (execution.successCount ?? 0) +
  (execution.warningCount ?? 0) +
  (execution.failureCount ?? 0);

/** The value each sortable column orders by. */
export const systemProcessExecutionSortValue = (
  execution: SystemProcessExecution,
  column: SystemProcessExecutionSortColumn
) => {
  switch (column) {
    case 'process':
      return execution.name || execution.processKey;
    case 'status':
      return execution.status;
    case 'type':
      return execution.type || execution.processDomain;
    case 'outcomes':
      return outcomeTotal(execution);
    case 'started':
      return execution.startDttm ?? 0;
  }
};

export const systemProcessExecutionTieBreak = (
  execution: SystemProcessExecution
) => execution.id;

export const filterSystemProcessExecutions = (
  executions: SystemProcessExecution[],
  filters: SystemProcessExecutionColumnFilters
): SystemProcessExecution[] => {
  const processQuery = filters.process.trim().toLowerCase();
  const typeQuery = filters.type.trim().toLowerCase();
  const filtered = executions.filter((execution) => {
    const processMatches =
      !processQuery ||
      `${execution.name} ${execution.processKey}`
        .toLowerCase()
        .includes(processQuery);
    const statusMatches =
      !filters.status || execution.status === filters.status;
    const typeMatches =
      !typeQuery ||
      `${execution.type} ${execution.processDomain}`
        .replace(/_/g, ' ')
        .toLowerCase()
        .includes(typeQuery);
    const outcomesMatch =
      filters.outcomes === '' ||
      (filters.outcomes === 'failed' && (execution.failureCount ?? 0) > 0) ||
      (filters.outcomes === 'warning' && (execution.warningCount ?? 0) > 0) ||
      (filters.outcomes === 'successful' &&
        (execution.failureCount ?? 0) === 0 &&
        (execution.warningCount ?? 0) === 0 &&
        (execution.successCount ?? 0) > 0);
    return processMatches && statusMatches && typeMatches && outcomesMatch;
  });

  return filtered;
};

export const filterAndSortSystemProcessExecutions = (
  executions: SystemProcessExecution[],
  state: SystemProcessExecutionTableState
): SystemProcessExecution[] =>
  sortOtsDataTableRows(
    filterSystemProcessExecutions(executions, state.filters),
    systemProcessExecutionSortValue,
    { key: state.sortColumn, direction: state.sortDirection },
    systemProcessExecutionTieBreak
  );
